import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X, ArrowUp, ArrowDown, Check, Flag } from "lucide-react";
import { L, Card, SectionTitle, Toggle, fmtDate, DateInput } from "./shared";

const EMPTY = { title: "", due_date: "", description: "", completed: false, completed_at: "" };

/** Repere & termene (milestones) — CRUD + ordonare; publicate clientului în „Calendar” și pe Acasă. */
export function MilestonesPanel({ pid, reload }) {
  const [items, setItems] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  async function load() {
    try { const { data } = await api.get(`/staff/client-config/${pid}/milestones`); setItems(data); }
    catch (e) { toast.error(apiErr(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  async function save() {
    if (!f.title.trim()) { toast.error("Titlul reperului este obligatoriu"); return; }
    setBusy(true);
    const payload = { ...f, due_date: f.due_date || null, completed_at: f.completed_at || null };
    try {
      if (editId) await api.put(`/staff/client-config/${pid}/milestones/${editId}`, payload);
      else await api.post(`/staff/client-config/${pid}/milestones`, payload);
      setF(EMPTY); setEditId(null); await load(); reload?.(); toast.success(editId ? "Reper actualizat" : "Reper adăugat");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  async function del(m) {
    if (!window.confirm(`Ștergi reperul „${m.title}”?`)) return;
    try { await api.delete(`/staff/client-config/${pid}/milestones/${m.id}`); await load(); reload?.(); toast.success("Reper șters"); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function toggleDone(m) {
    try {
      await api.put(`/staff/client-config/${pid}/milestones/${m.id}`, { title: m.title, due_date: m.due_date || null, description: m.description || "", completed: !m.completed, completed_at: null });
      await load(); reload?.();
    } catch (e) { toast.error(apiErr(e)); }
  }
  async function move(idx, dir) {
    const ids = items.map((m) => m.id); const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { const { data } = await api.put(`/staff/client-config/${pid}/milestones/reorder`, { ids }); setItems(data); reload?.(); }
    catch (e) { toast.error(apiErr(e)); }
  }
  function edit(m) { setEditId(m.id); setF({ title: m.title, due_date: m.due_date || "", description: m.description || "", completed: !!m.completed, completed_at: m.completed_at || "" }); }

  if (!items) return <div className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin" /> Se încarcă...</div>;
  const done = items.filter((m) => m.completed).length;

  return (
    <div className="space-y-6" data-testid="cc-milestones">
      <Card>
        <SectionTitle sub="Reperele apar la client în tab-ul „Calendar” (Repere & termene), în „Etape cheie” și „Repere contractuale următoare” pe Acasă. Marcarea ca finalizat notifică clientul.">{editId ? "Editează reperul" : "Adaugă reper"}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <L label="Titlu"><input value={f.title} onChange={set("title")} className="inp" placeholder="Ex: Autorizație de construire" data-testid="ms-title" /></L>
          <L label="Termen (dată)"><DateInput value={f.due_date} onChange={set("due_date")} testid="ms-due" /></L>
          <div className="md:col-span-2"><L label="Descriere (opțional)"><input value={f.description} onChange={set("description")} className="inp" data-testid="ms-desc" /></L></div>
          <Toggle checked={f.completed} onChange={set("completed")} label="Finalizat" testid="ms-completed" />
          {f.completed && <L label="Data finalizării (opțional)"><DateInput value={f.completed_at} onChange={set("completed_at")} testid="ms-completed-at" /></L>}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button onClick={save} disabled={busy} data-testid="ms-save" className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Salvează modificările" : "Adaugă"}
          </button>
          {editId && <button onClick={() => { setEditId(null); setF(EMPTY); }} className="inline-flex items-center gap-1 px-3 py-2 text-sm text-ink-muted hover:text-ink" data-testid="ms-cancel"><X className="h-4 w-4" /> Renunță</button>}
        </div>
      </Card>

      <Card>
        <SectionTitle sub="Ordinea de aici este ordinea afișată clientului.">Repere publicate ({done} / {items.length} finalizate)</SectionTitle>
        {items.length === 0 ? (
          <div className="py-10 text-center text-ink-muted text-sm border border-dashed border-neutral-200 rounded-lg">Niciun reper. Adaugă primul reper mai sus.</div>
        ) : (
          <ol className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg overflow-hidden">
            {items.map((m, i) => (
              <li key={m.id} className="px-4 py-3 flex items-center gap-3" data-testid={`ms-row-${m.id}`}>
                <button onClick={() => toggleDone(m)} title={m.completed ? "Marchează ca planificat" : "Marchează ca finalizat"} data-testid={`ms-toggle-${m.id}`}
                  className={`h-7 w-7 rounded-full grid place-items-center border shrink-0 ${m.completed ? "bg-emerald-600 border-emerald-600 text-white" : "border-neutral-300 text-neutral-400 hover:border-ink"}`}>
                  {m.completed ? <Check className="h-3.5 w-3.5" /> : <Flag className="h-3.5 w-3.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium truncate ${m.completed ? "text-ink-muted line-through" : "text-ink"}`}>{m.title}</div>
                  <div className="text-[11px] text-ink-muted font-mono">{m.due_date ? fmtDate(m.due_date) : "fără termen"}{m.completed && m.completed_at ? ` · finalizat ${fmtDate(m.completed_at)}` : ""}{m.description ? ` · ${m.description}` : ""}</div>
                </div>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-ink-muted hover:text-ink disabled:opacity-30" aria-label="Mută în sus" data-testid={`ms-up-${m.id}`}><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1.5 text-ink-muted hover:text-ink disabled:opacity-30" aria-label="Mută în jos" data-testid={`ms-down-${m.id}`}><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => edit(m)} className="p-1.5 text-ink-muted hover:text-ink" aria-label="Editează" data-testid={`ms-edit-${m.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(m)} className="p-1.5 text-ink-muted hover:text-red-600" aria-label="Șterge" data-testid={`ms-del-${m.id}`}><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
